import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Scale, MessageCircle, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import ContactPopup from './ContactPopup';
import MegaMenu from './MegaMenu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    {
      name: 'Services',
      path: '/services',
      dropdown: [
        { name: 'Trademark Registration', path: '/services/trademark-registration' },
        { name: 'Copyright Registration', path: '/services/copyright-registration' },
        { name: 'Company Formation', path: '/services/company-formation' },
        { name: 'Trademark Search', path: '/services/trademark-search' },
        { name: 'Amazon Brand Registry', path: '/services/amazon-brand-registry' }
      ]
    },
    { name: 'Contact Us', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
    {/* Top Bar - Contact Info */}
    <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2 hidden md:block">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-end items-center space-x-6 text-sm">
          <a 
            href="tel:+18332020812"
            className="flex items-center hover:text-amber-100 transition-colors"
          >
            <Phone className="h-4 w-4 mr-2" />
            <span className="font-medium">+1-833-202-0812</span>
          </a>
          <a 
            href="mailto:info@globalfilingprotection.com"
            className="flex items-center hover:text-amber-100 transition-colors"
          >
            <Mail className="h-4 w-4 mr-2" />
            <span className="font-medium">info@globalfilingprotection.com</span>
          </a>
        </div>
      </div>
    </div>

    <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-white/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="/logo-white.png" 
              alt="Global Filing Protection®" 
              className="h-10 w-auto transform transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation with Mega Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                isActive('/')
                  ? 'text-amber-500'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors ${
                isActive('/about')
                  ? 'text-amber-500'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              About Us
            </Link>
            <MegaMenu />
            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors ${
                isActive('/contact')
                  ? 'text-amber-500'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Contact Us
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center">
            <Button 
              onClick={() => {
                if (window.zE) {
                  window.zE('webWidget', 'open');
                }
              }}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-lg transform transition-transform hover:scale-105 chat-button"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Chat Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white p-2 rounded-lg hover:bg-slate-800 transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/20">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.path}
                    onClick={() => !link.dropdown && setIsMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-amber-500 bg-slate-800'
                        : 'text-gray-300 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    {link.name}
                  </Link>
                  {link.dropdown && (
                    <div className="ml-4 mt-2 space-y-1">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          onClick={() => setIsMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="space-y-2 mt-4 px-4">
                <a 
                  href="tel:+18332020812"
                  className="flex items-center justify-center py-3 text-amber-500 hover:text-amber-400 font-semibold transition-colors border border-amber-500/50 rounded-lg hover:bg-amber-500/10"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  <span>+1-833-202-0812</span>
                </a>
                <a 
                  href="mailto:info@globalfilingprotection.com"
                  className="flex items-center justify-center py-3 text-amber-500 hover:text-amber-400 font-semibold transition-colors border border-amber-500/50 rounded-lg hover:bg-amber-500/10"
                >
                  <Mail className="h-5 w-5 mr-2" />
                  <span>info@globalfilingprotection.com</span>
                </a>
              </div>
              <div className="space-y-2 mt-2 px-4">
                <Button 
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsPopupOpen(true);
                  }}
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-lg"
                >
                  Register My Trademark
                </Button>
                <Button 
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsPopupOpen(true);
                  }}
                  variant="outline"
                  className="w-full border-2 border-white/60 text-amber-500 hover:bg-amber-500/10 font-semibold"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Chat Now
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>

    <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default Header;
